import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { fmtCop, generateDiagnosticVoice } from '../lib/goals';

const NAVY = '#0A2540';
const GOLD = '#C9A961';
const SOFT = '#F9FAFB';

// Rentabilidad anual conservadora para las proyecciones (igual que el motor de metas).
const ANNUAL_RATE = 0.06;
// Sueños tipicos (cuota inicial / hito) en pesos — para traducir el ahorro a metas.
const DREAMS = [
  { icon: '✈️', name: 'Un viaje', cop: 5000000 },
  { icon: '🚗', name: 'Cuota inicial de un carro', cop: 15000000 },
  { icon: '🏠', name: 'Cuota inicial de una casa', cop: 40000000 },
];

// Tipos de deuda comunes en Colombia con su tasa MENSUAL tipica.
const DEBT_TYPES = [
  { v: 'tarjeta', t: 'Tarjeta de crédito', rate: 0.023 },
  { v: 'consumo', t: 'Crédito de consumo', rate: 0.018 },
  { v: 'gota', t: 'Gota a gota', rate: 0.15 },
  { v: 'otra', t: 'Otra', rate: 0.025 },
];
const DEBT_RATE = Object.fromEntries(DEBT_TYPES.map((d) => [d.v, d.rate]));

const round10k = (n) => Math.max(Math.round(Number(n || 0) / 10000) * 10000, 0);

// Meses para pagar una deuda de `debt` a tasa mensual `r` aportando `payment`.
function monthsToPayDebt(debt, r, payment) {
  if (payment <= 0 || payment <= debt * r) return Infinity; // no cubre ni intereses
  if (r === 0) return Math.ceil(debt / payment);
  return Math.ceil(-Math.log(1 - (debt * r) / payment) / Math.log(1 + r));
}

// Valor futuro de aportar `monthly` cada mes, a `rate` anual, por `years` anos.
function futureValue(monthly, rate, years) {
  const r = rate / 12, n = years * 12;
  return r === 0 ? monthly * n : monthly * ((Math.pow(1 + r, n) - 1) / r);
}
// Meses para acumular `target` aportando `monthly` (con interes compuesto).
function monthsToReach(target, monthly, rate) {
  if (monthly <= 0) return Infinity;
  const r = rate / 12;
  if (r === 0) return Math.ceil(target / monthly);
  const n = Math.log(1 + (target * r) / monthly) / Math.log(1 + r);
  return Math.ceil(n);
}
function fmtMonths(n) {
  if (!Number.isFinite(n)) return 'un buen tiempo';
  if (n < 12) return `~${n} mes${n !== 1 ? 'es' : ''}`;
  const y = Math.floor(n / 12), m = n % 12;
  if (m === 0) return `~${y} año${y > 1 ? 's' : ''}`;
  return `~${y} año${y > 1 ? 's' : ''} y ${m} mes${m > 1 ? 'es' : ''}`;
}

function Toggle({ value, onChange, options }) {
  return (
    <div className="flex gap-2 flex-wrap">
      {options.map((o) => (
        <button key={o.v} type="button" onClick={() => onChange(o.v)}
          className={`px-4 py-2 rounded-lg text-sm font-medium border transition ${value === o.v ? 'border-[#2A59C6] bg-[#EAF0FF] text-[#2A59C6]' : 'border-[#EFF1F4] bg-white text-[#374151] hover:border-[#2A59C6]'}`}>
          {o.t}
        </button>
      ))}
    </div>
  );
}

export default function GoalDiagnostic() {
  const navigate = useNavigate();
  const [income, setIncome] = useState('');
  const [fixed, setFixed] = useState('');
  const [fun, setFun] = useState('');
  const [hasDebt, setHasDebt] = useState(null);      // true | false
  const [debtType, setDebtType] = useState(null);    // 'tarjeta'|'consumo'|'gota'|'otra'
  const [debtAmount, setDebtAmount] = useState('');  // cuanto debe
  const [emergency, setEmergency] = useState(null);  // 'no' | 'algo' | 'si'
  const [r, setR] = useState(null);
  const [voiceLoading, setVoiceLoading] = useState(false);
  const [voiceUrl, setVoiceUrl] = useState(null);

  const handleVoice = async () => {
    if (!r || !r.ok || voiceLoading) return;
    setVoiceLoading(true);
    try {
      const res = await generateDiagnosticVoice({
        escalon: r.escalon, suggested: r.suggested, surplus: r.surplus,
        fv10: r.fv10, contributed10: r.contributed10, marketGave: r.marketGave,
        dreams: r.dreams, savingsRate: r.savingsRate, debtPlan: r.debtPlan,
        emergencyTarget: r.emergencyTarget, emergencyMonths: r.emergencyMonths,
      });
      if (res?.audioUrl) setVoiceUrl(`${res.audioUrl}?t=${Date.now()}`);
    } catch {} finally { setVoiceLoading(false); }
  };

  const calc = () => {
    setVoiceUrl(null); // nuevo diagnostico -> invalida el audio anterior
    const I = Number(income) || 0;
    const F = Number(fixed) || 0;
    const G = Number(fun) || 0;
    if (I <= 0) { setR({ ok: false }); return; }

    const surplus = Math.max(I - F - G, 0);
    const suggested = round10k(Math.min(surplus * 0.5, I * 0.2));
    const monthlyExpenses = F + G;

    // La escalera del dinero
    let escalon;
    if (surplus <= 0 || suggested <= 0) escalon = 1;
    else if (hasDebt === true) escalon = 2;
    else if (emergency !== 'si') escalon = 3;
    else escalon = 4;

    // Datos para el escalon 2 (plan de salida de deuda)
    let debtPlan = null;
    if (hasDebt === true && Number(debtAmount) > 0 && debtType) {
      const debt = Number(debtAmount);
      const dr = DEBT_RATE[debtType];
      const payment = suggested; // lo que destinaria a la deuda
      const monthlyInterest = Math.round(debt * dr);
      const covers = payment > debt * dr;
      const months = monthsToPayDebt(debt, dr, payment);
      const totalInterest = covers && Number.isFinite(months) ? Math.round(payment * months - debt) : null;
      const annualPct = Math.round((Math.pow(1 + dr, 12) - 1) * 100);
      const minPayment = round10k(debt * dr * 1.3); // sugerencia para salir si no cubre
      debtPlan = { debt, ratePct: (dr * 100).toFixed(1), monthlyInterest, covers, months, totalInterest, annualPct, payment, minPayment };
    }

    // Datos para el escalon 3 (fondo de emergencia = 3 meses de gastos)
    const emergencyTarget = round10k(monthlyExpenses * 3);
    const emergencyMonths = monthsToReach(emergencyTarget, suggested, 0); // colchon va liquido, sin invertir

    // Datos para el escalon 4 (las revelaciones)
    const fv10 = futureValue(suggested, ANNUAL_RATE, 10);
    const contributed10 = suggested * 120;
    const marketGave = Math.max(fv10 - contributed10, 0);
    const dreams = DREAMS.map((d) => ({ ...d, months: monthsToReach(d.cop, suggested, ANNUAL_RATE) }));
    const savingsRate = I > 0 ? Math.round((suggested / I) * 100) : 0;

    setR({ ok: true, income: I, surplus, suggested, monthlyExpenses, escalon, debtPlan,
      emergencyTarget, emergencyMonths, fv10, contributed10, marketGave, dreams, savingsRate });
  };

  const debtReady = hasDebt !== true || (debtType && Number(debtAmount) > 0);
  const ready = Number(income) > 0 && hasDebt !== null && emergency !== null && debtReady;

  return (
    <div className="px-4 sm:px-10 py-6 max-w-2xl mx-auto" style={{ background: SOFT }}>
      <button onClick={() => navigate('/dashboard/metas')} className="text-xs text-[#6B7280] hover:underline mb-4">← Mis Metas</button>
      <h1 className="text-xl font-bold text-[#181D27] mb-1">Tu diagnóstico financiero</h1>
      <p className="text-sm text-[#6B7280] mb-6">Cinco preguntas y te decimos cuánto puedes ahorrar, en qué orden, y en qué se convierte tu plata. Sin enredos.</p>

      <div className="bg-white border border-[#EFF1F4] rounded-xl px-6 py-5 space-y-5">
        <div>
          <label className="block text-sm text-[#181D27] mb-1">¿Cuánto te entra al mes?</label>
          <input type="number" inputMode="numeric" value={income} onChange={(e) => setIncome(e.target.value)} placeholder="2500000"
            className="w-full border border-[#EFF1F4] rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#2A59C6]" />
          {Number(income) > 0 && <div className="text-xs text-[#6B7280] mt-1">{fmtCop(income)}</div>}
        </div>
        <div>
          <label className="block text-sm text-[#181D27] mb-1">¿Cuánto se te va en lo fijo?</label>
          <div className="text-xs text-[#6B7280] mb-1">Arriendo, servicios, comida, transporte. (Las deudas van en la pregunta de abajo.)</div>
          <input type="number" inputMode="numeric" value={fixed} onChange={(e) => setFixed(e.target.value)} placeholder="1500000"
            className="w-full border border-[#EFF1F4] rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#2A59C6]" />
        </div>
        <div>
          <label className="block text-sm text-[#181D27] mb-1">¿Y en gustos?</label>
          <div className="text-xs text-[#6B7280] mb-1">Salidas, antojos, suscripciones.</div>
          <input type="number" inputMode="numeric" value={fun} onChange={(e) => setFun(e.target.value)} placeholder="400000"
            className="w-full border border-[#EFF1F4] rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#2A59C6]" />
        </div>
        <div>
          <label className="block text-sm text-[#181D27] mb-2">¿Tienes deudas caras? <span className="text-xs font-normal text-[#6B7280]">(tarjeta, gota a gota)</span></label>
          <Toggle value={hasDebt} onChange={setHasDebt} options={[{ v: false, t: 'No' }, { v: true, t: 'Sí' }]} />
          {hasDebt === true && (
            <div className="mt-3 space-y-3 rounded-lg bg-[#F9FAFB] border border-[#EFF1F4] p-3">
              <div>
                <label className="block text-xs text-[#6B7280] mb-1">¿De qué tipo?</label>
                <Toggle value={debtType} onChange={setDebtType} options={DEBT_TYPES.map((d) => ({ v: d.v, t: d.t }))} />
              </div>
              <div>
                <label className="block text-xs text-[#6B7280] mb-1">¿Cuánto debes en total?</label>
                <input type="number" inputMode="numeric" value={debtAmount} onChange={(e) => setDebtAmount(e.target.value)} placeholder="3000000"
                  className="w-full border border-[#EFF1F4] rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:border-[#2A59C6]" />
                {Number(debtAmount) > 0 && <div className="text-xs text-[#6B7280] mt-1">{fmtCop(debtAmount)}</div>}
              </div>
            </div>
          )}
        </div>
        <div>
          <label className="block text-sm text-[#181D27] mb-2">¿Tienes un colchón para emergencias?</label>
          <Toggle value={emergency} onChange={setEmergency} options={[{ v: 'no', t: 'No' }, { v: 'algo', t: 'Algo' }, { v: 'si', t: 'Sí, 3 meses o más' }]} />
        </div>

        <button onClick={calc} disabled={!ready}
          className="w-full py-2.5 rounded-lg text-white font-medium disabled:opacity-50" style={{ background: NAVY }}>
          Ver mi diagnóstico
        </button>
      </div>

      {/* ===== RESULTADO ===== */}
      {r && r.ok && (
        <div className="mt-4 space-y-4">
          {/* Escalon 1 — no le alcanza */}
          {r.escalon === 1 && (
            <div className="bg-white border border-[#EFF1F4] rounded-xl px-6 py-5">
              <div className="text-base font-semibold text-[#181D27] mb-1">Por ahora lo fijo y los gustos se llevan casi todo</div>
              <p className="text-sm text-[#6B7280] mb-4">No hay problema: empieza con un monto pequeño que te haga sentir cómodo, así sea $20.000 al mes. Lo importante es arrancar el hábito.</p>
              <button onClick={() => navigate('/dashboard/metas/nueva?ahorro=20000')} className="w-full py-2.5 rounded-lg text-white font-medium" style={{ background: NAVY }}>Crear mi primera meta</button>
            </div>
          )}

          {/* Escalon 2 — plan de salida de deuda con numeros reales */}
          {r.escalon === 2 && (
            <div className="rounded-xl px-6 py-5 text-white" style={{ background: `linear-gradient(135deg, ${NAVY} 0%, #1A3A6C 100%)` }}>
              <div className="text-[10px] uppercase tracking-wider font-bold mb-1" style={{ color: GOLD }}>Tu próximo mejor paso · Escalón 2 de 4</div>
              <div className="text-lg font-bold mb-2">Primero, salgamos de tu deuda</div>

              {r.debtPlan && (
                <>
                  <p className="text-sm text-white/80 mb-3">
                    Debes <b>{fmtCop(r.debtPlan.debt)}</b> a una tasa de <b>{r.debtPlan.ratePct}% mensual</b>. Eso hoy te cuesta cerca de <b style={{ color: GOLD }}>{fmtCop(r.debtPlan.monthlyInterest)} al mes</b> solo en intereses — plata que se va sin dejarte nada.
                  </p>

                  {r.debtPlan.covers ? (
                    <div className="rounded-lg p-3 mb-3 text-sm" style={{ background: 'rgba(255,255,255,0.08)' }}>
                      <div className="mb-1">Si destinas <b>{fmtCop(r.debtPlan.payment)}</b> al mes a pagarla:</div>
                      <div>• Sales en <b style={{ color: GOLD }}>{fmtMonths(r.debtPlan.months)}</b>.</div>
                      {r.debtPlan.totalInterest != null && <div>• Pagarías <b>{fmtCop(r.debtPlan.totalInterest)}</b> en intereses en total.</div>}
                      <div className="mt-1">• Pagar esta deuda te <b>rinde {r.debtPlan.annualPct}% al año</b> — ninguna inversión segura te da eso.</div>
                    </div>
                  ) : (
                    <div className="rounded-lg p-3 mb-3 text-sm" style={{ background: 'rgba(201,169,97,0.15)' }}>
                      Con {fmtCop(r.debtPlan.payment)} al mes no alcanzas ni a cubrir los intereses ({fmtCop(r.debtPlan.monthlyInterest)}). La deuda crecería. Intenta destinar al menos <b>{fmtCop(r.debtPlan.minPayment)}</b> al mes para empezar a bajarla.
                    </div>
                  )}

                  <p className="text-sm text-white/80 mb-3">Lo bueno: cuando salgas, esos <b>{fmtCop(r.debtPlan.payment)}</b> al mes se vuelven tu ahorro para tus sueños — y ahí sí, a invertir.</p>

                  <button onClick={() => navigate(`/dashboard/metas/nueva?ahorro=${r.debtPlan.payment}&nombre=${encodeURIComponent('Libre de deudas')}&monto=${r.debtPlan.debt}`)} className="px-4 py-2 rounded-lg text-sm font-semibold" style={{ background: GOLD, color: NAVY }}>Crear mi meta "Libre de deudas" →</button>
                </>
              )}
            </div>
          )}

          {/* Escalon 3 — fondo de emergencia */}
          {r.escalon === 3 && (
            <div className="rounded-xl px-6 py-5 text-white" style={{ background: `linear-gradient(135deg, ${NAVY} 0%, #1A3A6C 100%)` }}>
              <div className="text-[10px] uppercase tracking-wider font-bold mb-1" style={{ color: GOLD }}>Tu próximo mejor paso · Escalón 3 de 4</div>
              <div className="text-lg font-bold mb-2">Tu primer sueño: un colchón de tranquilidad</div>
              <p className="text-sm text-white/80 mb-3">Antes de invertir a largo plazo, ten un colchón para imprevistos (un diente, el carro, una urgencia). Para ti serían <b>{fmtCop(r.emergencyTarget)}</b> (3 meses de tus gastos). Con lo que puedes ahorrar, lo logras en <b>{fmtMonths(r.emergencyMonths)}</b>. Con eso firme, todo lo demás se disfruta sin miedo.</p>
              <button onClick={() => navigate(`/dashboard/metas/nueva?ahorro=${r.suggested}&nombre=${encodeURIComponent('Fondo de emergencia')}&monto=${r.emergencyTarget}`)} className="px-4 py-2 rounded-lg text-sm font-semibold" style={{ background: GOLD, color: NAVY }}>Crear mi "Fondo de emergencia" →</button>
            </div>
          )}

          {/* Escalon 4 — LISTO para invertir: las 3 revelaciones */}
          {r.escalon === 4 && (
            <>
              <div className="bg-white border border-[#EFF1F4] rounded-xl px-6 py-5">
                <div className="text-xs uppercase tracking-wider text-[#6B7280] mb-1">Puedes ahorrar cómodo</div>
                <div className="text-3xl font-bold text-[#181D27] mb-1">{fmtCop(r.suggested)} <span className="text-sm font-normal text-[#6B7280]">/ mes</span></div>
                <p className="text-sm text-[#6B7280]">Sin apretarte: dejamos un colchón sobre tu margen de {fmtCop(r.surplus)}.</p>
              </div>

              {/* Revelacion 1: interes compuesto */}
              <div className="rounded-xl px-6 py-5 text-white" style={{ background: `linear-gradient(135deg, ${NAVY} 0%, #1A3A6C 100%)` }}>
                <div className="text-[10px] uppercase tracking-wider font-bold mb-1" style={{ color: GOLD }}>Lo que el mercado hace por ti</div>
                <div className="text-sm text-white/80 mb-1">Si inviertes eso por 10 años, tendrás cerca de</div>
                <div className="text-3xl font-bold mb-2">{fmtCop(r.fv10)}</div>
                <p className="text-sm text-white/80">Tú pones <b>{fmtCop(r.contributed10)}</b>. El mercado te regala los otros <b style={{ color: GOLD }}>{fmtCop(r.marketGave)}</b>. No estás ahorrando solo: tu dinero ahorra contigo.</p>
              </div>

              {/* Revelacion 2: suenos con fecha */}
              <div className="bg-white border border-[#EFF1F4] rounded-xl px-6 py-5">
                <div className="text-sm font-bold text-[#0A2540] mb-3">Con eso, esto es lo que desbloqueas</div>
                <div className="space-y-2">
                  {r.dreams.map((d, i) => (
                    <div key={i} className="flex items-center justify-between text-sm">
                      <span className="text-[#374151]">{d.icon} {d.name}</span>
                      <span className="font-semibold text-[#0A2540]">{fmtMonths(d.months)}</span>
                    </div>
                  ))}
                </div>
                <div className="text-[11px] text-[#9CA3AF] mt-3">Estimado con rentabilidad conservadora ({(ANNUAL_RATE * 100).toFixed(0)}% anual). No es una promesa de rendimiento.</div>
              </div>

              {/* Revelacion 3: salud financiera */}
              <div className="bg-white border border-[#EFF1F4] rounded-xl px-6 py-5">
                <div className="text-sm font-bold text-[#0A2540] mb-1">Tu salud de ahorro</div>
                <p className="text-xs text-[#6B7280] mb-2">De cada $100 que ganas, ahorrarías {r.savingsRate}. Lo saludable es 20.</p>
                <div className="h-2.5 rounded-full bg-[#EFF1F4] overflow-hidden">
                  <div className="h-full rounded-full" style={{ width: `${Math.min(r.savingsRate / 20 * 100, 100)}%`, background: r.savingsRate >= 20 ? '#16A34A' : GOLD }} />
                </div>
                <div className="text-[11px] text-[#9CA3AF] mt-1">{r.savingsRate >= 20 ? '¡Vas excelente! Por encima de lo saludable.' : 'Vas bien. Subir de a poco te acerca a la meta del 20%.'}</div>
              </div>

              <button onClick={() => navigate(`/dashboard/metas/nueva?ahorro=${r.suggested}`)} className="w-full py-3 rounded-lg text-white font-semibold" style={{ background: NAVY }}>Crear una meta con esto →</button>
            </>
          )}
        </div>
      )}

      {/* Escuchar el diagnostico con voz (Daniela) */}
      {r && r.ok && (
        <div className="mt-4">
          <button onClick={handleVoice} disabled={voiceLoading}
            className="flex items-center justify-center gap-2 w-full py-3 rounded-xl text-white text-sm font-medium disabled:opacity-60" style={{ background: NAVY }}>
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z"/></svg>
            {voiceLoading ? 'Generando con voz...' : 'Escuchar mi diagnóstico con el Oráculo'}
          </button>
          {voiceUrl && <audio controls autoPlay preload="none" src={voiceUrl} className="w-full mt-3" style={{ height: 40 }} />}
        </div>
      )}

      {r && !r.ok && <div className="text-sm text-red-600 mt-3">Cuéntanos cuánto te entra al mes para poder ayudarte.</div>}
    </div>
  );
}
